import BackgroundImage from '@/components/BackgroundImage.vue'
import Stamp from '@/components/Stamp/index.vue'
import { api } from '@/galaxy-cruise/api'
import { IAnswer, IGalaxy, ILevel, ICampaign, SeriesId } from '@/galaxy-cruise/api_interface'
import { asyncAlert } from '@/galaxy-cruise/components/AsyncAlert'
import { coordFromGalaxy, reloadSession } from '@/galaxy-cruise/utils'
import { ChangeHook } from '@/galaxy-cruise/utils/change_hook'
import { store } from '@/store'
import { ringsTract } from '@/utils/tract'
import { EquatorialCoord } from '@hscmap/angle'
import * as easing from '@hscmap/easing'
import { Component, Vue } from "vue-property-decorator"
import ImageButton from './ImageButton.vue'
import CabinUpgrade from "./MessageWindows/CabinUpgrade.vue"
import LevelDone from "./MessageWindows/LevelDone.vue"
import NewSpecialityGoods from "./MessageWindows/NewSpecialityGoods.vue"
import TractDone from "./MessageWindows/TractDone.vue"
import CaptainAnswer from './MessageWindows/CaptainAnswer.vue'
import SeriesDoneDefault from "./MessageWindows/SeriesDone/Default/index.vue"
import SeriesDoneTraining from "./MessageWindows/SeriesDone/Training/index.vue"
import { deepEqual } from '@/utils/deep'

interface IAnswerResponse {
  answer: IAnswer
  quest_done: boolean
  level_done: boolean
  campaigns: ICampaign[]
}

@Component({
  components: { BackgroundImage, Stamp, ImageButton },
})
export default class extends Vue {
  private unsetCameraMoveHook!: () => void
  show = false
  step = 1

  answerId: null | string = null
  galaxy_type: IAnswer["galaxy_type"] = 'not_sure'
  in_interaction: IAnswer["in_interaction"] = 'not_sure'
  ring = false
  fan = false
  tail = false
  distorted = false

  get ts(): number { // timescale
    // @ts-ignore
    return (process.env.NODE_ENV === 'development') ? 0.125 : 1.
  }

  mounted() {
    this.unsetCameraMoveHook = this.setCameraMoveHook()
    this.$watch(() => this.gc.targetGalaxy, (g) => {
      if (g) {
        const a = this.gc.galId2answer[g.id]
        this.goStep(1)
        if (a) {
          this.answerId = a.id
          this.galaxy_type = a.galaxy_type
          this.in_interaction = a.in_interaction
          this.ring = a.ring
          this.fan = a.fan
          this.tail = a.tail
          this.distorted = a.distorted
        } else {
          this.reset()
        }
      }
    })
  }

  private reset() {
    this.answerId = null
    this.galaxy_type = 'not_sure'
    this.in_interaction = 'not_sure'
    this.ring = false
    this.fan = false
    this.tail = false
    this.distorted = false
    this.goStep(1)
  }

  beforeDestroy() {
    this.unsetCameraMoveHook()
  }

  step1(galaxy_type: IAnswer["galaxy_type"]) {
    this.galaxy_type = galaxy_type
    this.goStep(2)
  }

  step2(in_interaction: IAnswer["in_interaction"]) {
    this.in_interaction = in_interaction
    if (this.in_interaction === 'yes') {
      this.goStep(3)
    } else {
      this.ring = false
      this.fan = false
      this.tail = false
      this.distorted = false
      this.goStep(4)
    }
  }

  step3(flatName: string) {
    const self = this as any
    self[flatName] = !self[flatName]
  }

  transitionName = 'slide-left'

  goStep(n: number) {
    this.transitionName = n < this.step ? 'slide-right' : 'slide-left'
    this.step = n
  }

  async submit() {
    const { quest_done, level_done } = await this.postAnswer()
    if (quest_done) {
      this.nextQuest(level_done)
    } else {
      this.goToNextGalaxy()
    }
  }

  private async postAnswer() {
    const axios = api.default
    const { galaxy_type, in_interaction, ring, fan, tail, distorted } = this
    const galaxy_id = this.gc.targetGalaxy!.id
    await this.showCaptainAnswer({ id: '', galaxy_id, galaxy_type, in_interaction, ring, fan, tail, distorted })

    const res = await axios.post(`../api/v1/quests/${this.quest!.id}/answers`, {
      answer: { galaxy_id, galaxy_type, in_interaction, ring, fan, tail, distorted },
    })
    const resData: IAnswerResponse = res.data
    const { answer: { id }, quest_done, level_done, campaigns: newCampaigns } = resData
    await updateCampaigns(newCampaigns)
    this.answerId = id
    const answer: IAnswer = { id, galaxy_id, galaxy_type, in_interaction, ring, fan, tail, distorted }
    this.quest!.answers.push(answer)
    if (this.quest.answers.length < this.quest.galaxies.length) {
      const remaining = this.quest.galaxies.length - this.quest.answers.length
      const message = this.$i18n.tc('galaxy_done', remaining, { remaining }) as string
      await this.showStamp(message)
    }
    const globe = store.currentGlobe()
    await globe.viewFactory.jumpTo(
      { fovy: 0.001 },
      { duration: 1000 * this.ts, coord: coordFromGalaxy(this.gc.targetGalaxy!) }).promise
    return { quest_done, level_done }
  }

  private async showCaptainAnswer(answer: IAnswer) {
    const galaxy = this.quest.galaxies.find((g) => g.id === answer.galaxy_id)!
    if (galaxy.captain_answer) {
      await asyncAlert({ component: CaptainAnswer, closable: false, props: { answer, galaxy } })
    }
  }

  private async goToNextGalaxy() {
    const globe = store.currentGlobe()
    const g = this.gc.nextQuestion
    if (g) {
      await globe.viewFactory.jumpTo(
        { fovy: 0.0005 },
        { duration: 3000 * this.ts, coord: coordFromGalaxy(g), easingFunction: easing.slowStartStop4 }).promise
    }
  }

  private async goTract(tractId: number) {
    this.gc.targetGalaxy = null
    const tract_id = this.quest!.tract_id
    const [a, d] = ringsTract.index2ad(tract_id)
    const globe = store.currentGlobe()
    await globe.viewFactory.jumpTo(
      { fovy: 0.02 },
      { duration: 5000 * this.ts, coord: EquatorialCoord.fromRad(a, d), easingFunction: easing.slowStartStop4 }).promise
  }

  private async nextQuest(level_done: boolean) {
    await this.showStamp('Area Completed !')

    const globe = store.currentGlobe()
    await globe.viewFactory.jumpTo(
      { fovy: 0.001 },
      { duration: 1000 * this.ts, coord: coordFromGalaxy(this.gc.targetGalaxy!) }).promise
    await this.goTract(this.quest.tract_id)

    await asyncAlert({ component: TractDone, props: {}, closable: false })
    const hooks = new ChangeHook()

    hooks.watch(() => this.gc.cabinGrade, async () => {
      await asyncAlert({ component: CabinUpgrade, closable: false })
    })
    hooks.watch(() => this.gc.specialityGoodsCount, async () => {
      await asyncAlert({ component: NewSpecialityGoods, closable: false })
    })

    function levelName(level: ILevel, locale: string) {
      return (level as any)[`${locale}_name`]
    }

    // レベルアップ確認
    hooks.watch(() => this.quest.level, async (before) => {
      if (this.quest.level.series_id == SeriesId.infinite) {
        return
      }
      await asyncAlert({ component: LevelDone, props: { prevLevelName: levelName(before, this.$i18n.locale) }, closable: false })
    }, { deep: true })

    const seriesDone = {}

    // シリーズ完了
    hooks.watch(() => this.gc.seriesDone, async (before, after) => {
      if (this.quest.level.series_id == SeriesId.infinite) {
        return
      }
      if (after) {
        const component = { training: SeriesDoneTraining, season_1: SeriesDoneDefault, infinite: null }[this.gc.series!]
        await asyncAlert({ component: LevelDone, props: { prevLevelName: levelName(this.quest.level, this.$i18n.locale) }, closable: false })
        await asyncAlert({ component, closable: false })
        return seriesDone
      }
    })

    // シリーズ完了の場合はここで抜ける
    if ((await hooks.flush(async () => {
      await reloadSession()
    })).slice(-1)[0] === seriesDone) {
      return
    }

    const nextTractId = this.quest!.tract_id
    await this.goTract(nextTractId)
    this.gc.targetGalaxy = null
    const g = this.gc.nextQuestion
    if (g) {
      await globe.viewFactory.jumpTo(
        { fovy: 0.0005 },
        { duration: 3000 * this.ts, coord: coordFromGalaxy(g), easingFunction: easing.slowStartStop4 }).promise
    }
  }

  private onCameraMoveStart() {
  }

  private onCameraMoveEnd() {
    this.refreshCurrentGalaxy()
    this.refreshPanelShow()
  }

  private refreshPanelShow() {
    const maxFoxy = 0.005
    const minDotProduct = Math.cos(store.currentFrame.view.fovy)
    this.show = store.currentFrame.view.fovy < maxFoxy &&
      dotProdctOfCenterAnd(this.gc.targetGalaxy!) >= minDotProduct
  }

  private refreshCurrentGalaxy() {
    if (this.quest) {
      this.gc.targetGalaxy = this.quest.galaxies.slice().sort((g1, g2) => {
        return dotProdctOfCenterAnd(g2) - dotProdctOfCenterAnd(g1)
      })[0]
    }
  }

  get quest() {
    return this.gc.session!.quest
  }

  get gc() {
    return store.galaxyCruise
  }

  private setCameraMoveHook() {
    const globe = store.currentGlobe()
    const camraMoveStart = () => this.onCameraMoveStart()
    const cameraMoveEnd = () => this.onCameraMoveEnd()
    globe.ee.addListener('camera-move-start', camraMoveStart)
    globe.ee.addListener('camera-move-end', cameraMoveEnd)
    return () => {
      globe.ee.removeListener('camera-move-start', camraMoveStart)
      globe.ee.removeListener('camera-move-end', cameraMoveEnd)
    }
  }

  image(file: string) {
    return `./images/question-panel/${file}.png`
  }

  private stamp: string | null = null
  private stampResolve!: () => void

  showStamp(content: string) {
    this.stamp = content
    return new Promise<void>((resolve) => {
      this.stampResolve = () => resolve()
    })
  }

  stampDone() {
    this.stamp = null
    this.stampResolve()
  }
}

function dotProduct(a: EquatorialCoord, b: EquatorialCoord) {
  let p = 0
  const axyz = a.xyz
  const bxyz = b.xyz
  for (let i = 0; i < 3; ++i) {
    p += axyz[i] * bxyz[i]
  }
  return p
}

function dotProdctOfCenterAnd(galaxy: IGalaxy) {
  const { a, d } = store.currentFrame.view
  const center = EquatorialCoord.fromRad(a, d)
  const { ra, dec } = galaxy
  const coord = EquatorialCoord.fromDeg(ra, dec)
  return dotProduct(center, coord)
}


import GotMedal from './GotMedal.vue'

async function updateCampaigns(newCampaigns: ICampaign[]) {
  // この関数は分類完了時に呼ばれる。
  // answers#createがcampaignも返し、その中にキャンペーンの分類数が含まれる。
  // ちなみにキャンペーンの最初のメダルの獲得条件が0の場合は「メダル獲得アラート」はでない
  const oCampaigns = store.galaxyCruise.session!.campaigns
  const oState = oCampaigns.map(c => c.medals.map(m => c.count >= m.count))
  for (const nc of newCampaigns) {
    const i = oCampaigns.map(c => c.id).indexOf(nc.id)
    if (i < 0) {
      oCampaigns.push(nc)
    }
    else {
      Vue.set(oCampaigns, i, nc)
    }
  }
  // oState, nState は達成状況リスト
  // これが変化したということは新たにメダルを獲得したということ
  const nState = store.galaxyCruise.session!.campaigns.map(c => c.medals.map(m => c.count >= m.count))
  if (!deepEqual(oState, nState)) {
    const campaignDone = oState.length === nState.length && (() => {
      for (let i = 0; i < oState.length; ++i) {
        if (!deepEqual(oState[i], nState[i]) && nState[i].every(d => d))
          return true
      }
    })()
    const change = oState.length === nState.length && (() => {
      for (let i = 0; i < oState.length; ++i) {
        if (!deepEqual(oState[i], nState[i]) && oState[i].length === nState[i].length) {
          for (let j = 0; j < oState[i].length; ++j) {
            if (!oState[i][j] && nState[i][j]) {
              const campaign = store.galaxyCruise.session!.campaigns[i]
              return { campaign, medal: campaign.medals[j] }
            }
          }
        }
      }
    })()
    if (change) {
      const { medal, campaign } = change
      await asyncAlert({ component: GotMedal, closable: false, props: { campaign, campaignDone, newMedal: medal } })
    }
  }
}
